import { useAuthStore } from "../stores/auth";
import eye from "../assets/icons/eye.svg";
import eyeSlash from "../assets/icons/eye-off.svg";
import { useNavigate } from "react-router-dom";
import { login } from "../api/login";
import { showToastError } from "../services/toast";
import useModals from "../hooks/useModals";
import ResetPasswordModal from "../components/login/ResetPasswordModal";
import Verify2FA from "../components/login/Verify2FA";
import { useUserContext } from "../contexts/userContext";
import { Fragment, useState } from "react";
import {
  AuthCopyright,
  AuthField,
  AuthFooterLink,
  AuthShell,
  AuthSubmit,
  PasswordToggle,
} from "../components/ui/page/AuthShell";

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Store
  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
  } = useAuthStore();

  //Hooks
  const { modals, openModals, openModal, closeModal } = useModals();

  //Context
  const { refreshUserData } = useUserContext();

  // Fonction pour soumettre le formulaire de connexion
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    // Appel API
    login(email, password)
      .then(async (res) => {
        // Après connexion réussie, rafraîchir les données utilisateur depuis l'API
        await refreshUserData();
        
        // Les données seront automatiquement mises à jour via le UserContext
        if (res.data.typeRole === "Customer") {
          navigate("/customer-area/dashboard");
        } else {
          navigate("/dashboard");
        }
        setPassword("");
        setEmail("");
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response.data.mfa_sent === true) {
          openModal("2FA", <Verify2FA closeModal={() => closeModal("2FA")} />);
        } else if (error.response.data.error === "Mot de passe incorrect") {
          showToastError("Identifiant ou mot de passe incorrect");
        } else {
          showToastError("Erreur lors de la connexion");
        }
      });
  };

  return (
    <>
      <AuthShell
        eyebrow="Accédez à votre espace en toute sécurité"
        headline="Dynamic change through advice."
        description="Simplifiez vos opérations."
        title="Connexion"
        subtitle="Renseignez vos identifiants pour continuer."
        footer={
          <>
            <AuthFooterLink onClick={() => navigate("/cgu")}>
              CGU
            </AuthFooterLink>
            <span aria-hidden="true" className="text-slate-300">
              •
            </span>
            <AuthCopyright />
          </>
        }
      >
        <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
          <AuthField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            required
            autoComplete="email"
          />

          <AuthField
            label="Mot de passe"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={setPassword}
            required
            autoComplete="current-password"
            trailing={
              <PasswordToggle
                visible={showPassword}
                onToggle={() => setShowPassword(!showPassword)}
                icon={showPassword ? eye : eyeSlash}
              />
            }
          />

          <AuthSubmit isLoading={isLoading} loadingLabel="Connexion...">
            Se connecter
          </AuthSubmit>

          {/* Modal pour reset le password */}
          <button
            type="button"
            className="cursor-pointer self-start font-Nunito text-sm font-semibold text-indigo-600 transition-colors hover:text-indigo-800"
            onClick={() => {
              openModal(
                "resetPasswordModal",
                <ResetPasswordModal
                  closeModal={() => closeModal("resetPasswordModal")}
                />,
              );
            }}
          >
            Mot de passe oublié ?
          </button>
        </form>
      </AuthShell>

      {/* Affichage des modales en dehors de la grille */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? (
          <Fragment key={modalId}>{modals[modalId]}</Fragment>
        ) : null,
      )}
    </>
  );
};

export default Login;
